import { inject, Injectable } from '@angular/core';
import { isAnalyticsCookiesAllowed } from '../../services/cookie-consent.storage';
import { GtmService } from '../../shared/services/gtm.service';
import {
  DEFAULT_GA_ECOMMERCE_CURRENCY,
  GaEcommerceCurrency,
  GaEcommerceItem,
} from './ga-ecommerce.types';
import { roundMoney } from './ga-ecommerce.mapper';

export type GaEcommercePurchaseParams = {
  transaction_id: string;
  value: number;
  currency?: GaEcommerceCurrency;
  items: GaEcommerceItem[];
};

export type GaEcommerceCheckoutParams = {
  value: number;
  currency?: GaEcommerceCurrency;
  items: GaEcommerceItem[];
};

@Injectable({
  providedIn: 'root',
})
export class GaEcommerceService {
  private readonly gtm = inject(GtmService);

  viewItemList(items: GaEcommerceItem[], currency: GaEcommerceCurrency = DEFAULT_GA_ECOMMERCE_CURRENCY): void {
    this.send('view_item_list', { items, currency });
  }

  viewItem(item: GaEcommerceItem, currency: GaEcommerceCurrency = DEFAULT_GA_ECOMMERCE_CURRENCY): void {
    this.send('view_item', { items: [item], currency, value: roundMoney(item.price * item.quantity) });
  }

  selectItem(item: GaEcommerceItem, currency: GaEcommerceCurrency = DEFAULT_GA_ECOMMERCE_CURRENCY): void {
    this.send('select_item', { items: [item], currency, value: roundMoney(item.price * item.quantity) });
  }

  beginCheckout(params: GaEcommerceCheckoutParams): void {
    this.send('begin_checkout', {
      items: params.items,
      currency: params.currency ?? DEFAULT_GA_ECOMMERCE_CURRENCY,
      value: params.value,
    });
  }

  purchase(params: GaEcommercePurchaseParams): void {
    this.send('purchase', {
      items: params.items,
      currency: params.currency ?? DEFAULT_GA_ECOMMERCE_CURRENCY,
      value: params.value,
      transaction_id: params.transaction_id,
    });
  }

  private send(
    eventName: string,
    params: {
      items: GaEcommerceItem[];
      currency: GaEcommerceCurrency;
      value?: number;
      transaction_id?: string;
    },
  ): void {
    if (!isAnalyticsCookiesAllowed()) {
      return;
    }

    const value = params.value ?? this.sumItemsValue(params.items);

    const ecommerce: Record<string, unknown> = {
      currency: params.currency,
      value: roundMoney(value),
      items: params.items.map((item) => ({
        item_id: item.item_id,
        item_name: item.item_name,
        price: item.price,
        quantity: item.quantity,
        ...(item.item_category ? { item_category: item.item_category } : {}),
        ...(item.item_variant ? { item_variant: item.item_variant } : {}),
      })),
    };

    if (params.transaction_id) {
      ecommerce['transaction_id'] = params.transaction_id;
    }

    this.gtm.push({
      event: eventName,
      ecommerce,
    });
  }

  private sumItemsValue(items: GaEcommerceItem[]): number {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }
}
